import { AvailabilityStatus } from '@fdc/__generated__/graphql';
import { getInitialAddValue } from '@fdc/components/ui/product-tile/add-to-cart-helpers';
import { GTM_EVENT_TYPES, NOT_SET } from '@fdc/constants/analytics-constants';
import {
  ProductAnalyticsOptions,
  ViewItemEcommercePayload,
  ViewItemListEcommerceItem,
  ViewItemListEcommercePayload,
} from '@fdc/constants/analytics-types';
import { ProductSummaryUnion } from '@fdc/types';
import { pushGtmAnalyticsEvent } from '@fdc/utils/analytics-utils';
import {
  getHwwStoreContext,
  getItemAttribution,
  resolveViewItemAttribution,
} from '@fdc/utils/item-attribution-analytics';
import {
  getItemTag,
  resolveAlgoliaObjectId,
} from '@fdc/utils/product-analytics-utils';
import {
  getProductAdId,
  isSponsoredProduct,
  resolveActiveVariantForAnalytics,
} from '@fdc/utils/product-summary.mapper';

const CURRENCY = 'NZD';
const DEFAULT_QUANTITY = 1;
export { NOT_SET };

const OWN_BRAND_NAMES = ['woolworths', 'countdown', 'macro'];

type VariantSummary = ProductSummaryUnion['variants'][number];

/**
 * Derives availability enum from variant availabilityStatus or stockOnHand.
 */
function getItemAvailability(variant?: VariantSummary): string {
  const status = variant?.availabilityStatus;
  if (status === AvailabilityStatus.InStock) {
    return AvailabilityStatus.InStock;
  }
  if (status === AvailabilityStatus.OutOfStock) {
    return AvailabilityStatus.OutOfStock;
  }

  const stock = variant?.stockOnHand;
  if (stock == null) {
    return NOT_SET;
  }
  return stock > 0 ? AvailabilityStatus.InStock : AvailabilityStatus.OutOfStock;
}

export function getPrice(variant?: VariantSummary): number {
  const variantPrice = variant?.variantPrice;
  if (typeof variantPrice?.sellingPrice === 'number') {
    return variantPrice.sellingPrice;
  }

  const priceList = variant?.price;
  const firstPrice = priceList?.[0];
  const basePrice = firstPrice?.pricingInfo?.[0]?.basePrice;
  if (typeof basePrice === 'number' && !Number.isNaN(basePrice)) {
    return basePrice;
  }
  return 0;
}

export function getDiscount(variant?: VariantSummary): number {
  const variantPrice = variant?.variantPrice;
  if (typeof variantPrice?.savedAmount === 'number') {
    return variantPrice.savedAmount;
  }

  const priceList = variant?.price;
  const firstPrice = priceList?.[0];
  const discount = firstPrice?.pricingInfo?.[0]?.discountPrice;
  if (typeof discount === 'number' && !Number.isNaN(discount)) {
    return discount;
  }
  return 0;
}

function getItemUnit(variant?: VariantSummary): string {
  const unit = variant?.purchaseUnit?.unit;
  if (!unit) {
    return NOT_SET;
  }
  return unit.toLowerCase() === 'ea' ? 'each' : unit.toLowerCase();
}

export function getItemVariant(variant?: VariantSummary): string | null {
  return variant?.variantPrice?.sellingUnit ?? variant?.name ?? null;
}

/** PDP view_item quantity reflects UI default (1 for each, bulk default for weight). */
function getViewItemQuantity(
  variant?: VariantSummary,
  options?: ProductAnalyticsOptions
): number {
  if (options?.cartQuantity != null && options.cartQuantity > 0) {
    return options.cartQuantity;
  }

  const purchaseUnit = variant?.purchaseUnit;
  if (!purchaseUnit) {
    return DEFAULT_QUANTITY;
  }

  const unit = purchaseUnit.unit?.toLowerCase();
  const type = unit === 'kg' ? 'weight' : 'quantity';
  return getInitialAddValue(type, purchaseUnit);
}

export function isOwnBrand(
  product: Partial<ProductSummaryUnion>,
  override?: boolean
): boolean {
  if (override !== undefined) {
    return override;
  }

  const brand = product.brand?.trim().toLowerCase();
  if (!brand) {
    return false;
  }
  return OWN_BRAND_NAMES.some(
    (ownBrand) => brand === ownBrand || brand.startsWith(`${ownBrand} `)
  );
}

const CATALOG_ROOT_CATEGORY_NAME = 'All Departments';

export function getCategoryLevels(product: Partial<ProductSummaryUnion>): {
  item_category: string | null;
  item_category2: string | null;
  item_category3: string | null;
} {
  const names = product.categoryHierarchyNames;
  // Algolia includes the catalog root as lvl0; department starts at lvl1.
  const levels = [
    names?.lvl0?.[0],
    names?.lvl1?.[0],
    names?.lvl2?.[0],
    names?.lvl3?.[0],
  ].filter(
    (name): name is string =>
      Boolean(name) && name !== CATALOG_ROOT_CATEGORY_NAME
  );

  return {
    item_category: levels[0] ?? null,
    item_category2: levels[1] ?? null,
    item_category3: levels[2] ?? null,
  };
}

/**
 * Maps a single ProductSummary to the view_item_list ecommerce item shape.
 * Promotion context must be passed explicitly; sessionStorage is not read here
 * to avoid stale attribution on unrelated list impressions.
 */
export function mapProductSummaryToViewItemListItem(
  product: Partial<ProductSummaryUnion>,
  index: number,
  itemListId: string,
  itemListName: string,
  options?: ProductAnalyticsOptions
): ViewItemListEcommerceItem {
  const variant = resolveActiveVariantForAnalytics(product, {
    cartSelectedUom: options?.cartSelectedUom,
    cartQuantity: options?.cartQuantity,
  });
  const categories = getCategoryLevels(product);
  const storeContext = getHwwStoreContext();
  const variantPrice = variant?.variantPrice;
  const promotion = options?.promotion;
  const sortBy = options?.sortBy;
  const adId = options?.adId ?? getProductAdId(product) ?? null;
  const objectId = resolveAlgoliaObjectId(
    product.sku,
    product.storeKey,
    options?.algoliaSearchIndex
  );

  return {
    item_id: product.sku ?? NOT_SET,
    item_name: product.productName ?? NOT_SET,
    affiliation: storeContext.affiliation,
    currency: CURRENCY,
    discount: getDiscount(variant),
    index: index + 1,
    is_alcohol: !!product.isAlcohol,
    is_own_brand: isOwnBrand(product, options?.isOwnBrand),
    is_sponsored: isSponsoredProduct(product),
    is_tobacco: !!product.isTobacco,
    item_brand: product.brand ?? NOT_SET,
    item_category: categories.item_category ?? NOT_SET,
    item_category2: categories.item_category2 ?? NOT_SET,
    item_category3: categories.item_category3 ?? NOT_SET,
    item_list_name: itemListName,
    item_list_id: itemListId,
    item_unit: getItemUnit(variant),
    item_variant: getItemVariant(variant),
    location_id: storeContext.location_id,
    price: getPrice(variant),
    quantity: DEFAULT_QUANTITY,
    sort_by: sortBy,
    item_availability: getItemAvailability(variant),
    is_boost_offer: !!variantPrice?.isBoostOffer,
    is_club_price: !!variantPrice?.isClubPrice,
    promotion_id: promotion?.promotion_id ?? null,
    promotion_name: promotion?.promotion_name ?? null,
    creative_name: promotion?.creative_name ?? null,
    creative_slot: promotion?.creative_slot ?? null,
    item_tag: getItemTag(product),
    algolia_search_index: options?.algoliaSearchIndex ?? null,
    object_id: objectId,
    ad_id: adId,
  };
}

/**
 * Builds the full view_item_list ecommerce payload from a list of ProductSummary.
 */
export function buildViewItemListPayload(
  products: ProductSummaryUnion[],
  itemListId: string,
  itemListName: string,
  options?: ProductAnalyticsOptions
): ViewItemListEcommercePayload {
  const items: ViewItemListEcommerceItem[] = products.map((product, index) =>
    mapProductSummaryToViewItemListItem(
      product,
      index,
      itemListId,
      itemListName,
      options
    )
  );
  return {
    item_list_id: itemListId,
    item_list_name: itemListName,
    sort_by: options?.sortBy,
    algolia_query_id: options?.queryId ?? null,
    items,
  };
}

/**
 * Pushes view_item_list to the data layer.
 * Clears the previous ecommerce object then pushes the event as per GA4 spec.
 */
export function pushViewItemListEvent(
  payload: ViewItemListEcommercePayload
): void {
  if (typeof window === 'undefined') {
    return;
  }
  pushGtmAnalyticsEvent({
    event: GTM_EVENT_TYPES.VIEW_ITEM_LIST,
    ecommerce: payload,
  });
}

export function buildViewItemPayload(
  product: Partial<ProductSummaryUnion>,
  options?: ProductAnalyticsOptions
): ViewItemEcommercePayload {
  const stored = getItemAttribution();
  const { list, options: storedOptions } = resolveViewItemAttribution(
    product.sku,
    stored
  );
  const analyticsOptions: ProductAnalyticsOptions = {
    ...storedOptions,
    ...options,
    sortBy: options?.sortBy ?? storedOptions.sortBy,
    promotion: options?.promotion ?? storedOptions.promotion,
    adId: options?.adId ?? storedOptions.adId,
    isOwnBrand: options?.isOwnBrand ?? storedOptions.isOwnBrand,
  };

  const variant = resolveActiveVariantForAnalytics(product, analyticsOptions);
  const quantity = getViewItemQuantity(variant, analyticsOptions);
  const listIndex = list ? Math.max(0, list.index - 1) : 0;

  const mappedItem = mapProductSummaryToViewItemListItem(
    product,
    listIndex,
    list?.item_list_id ?? NOT_SET,
    list?.item_list_name ?? NOT_SET,
    {
      ...analyticsOptions,
      sortBy: list ? analyticsOptions.sortBy : undefined,
    }
  );

  const item = {
    ...mappedItem,
    quantity,
    ...(list
      ? {}
      : {
          index: undefined,
          sort_by: undefined,
          item_list_id: NOT_SET,
          item_list_name: NOT_SET,
        }),
  };

  const payload: ViewItemEcommercePayload = {
    currency: CURRENCY,
    value: item.price * quantity,
    items: [item],
  };

  if (list) {
    payload.item_list_id = list.item_list_id;
    payload.item_list_name = list.item_list_name;
    if (analyticsOptions.sortBy) {
      payload.sort_by = analyticsOptions.sortBy;
    }
  }

  return payload;
}

export function pushViewItemEvent(payload: ViewItemEcommercePayload): void {
  if (typeof window === 'undefined') {
    return;
  }
  pushGtmAnalyticsEvent({
    event: GTM_EVENT_TYPES.VIEW_ITEM,
    ecommerce: payload,
  });
}
